import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, Loader2, IdCard } from "lucide-react";

export default function VerifyIdentity() {
  const { user, loading: authLoading } = useAuth();
  const [params] = useSearchParams();
  const [status, setStatus] = useState("loading"); // loading | not_started | processing | requires_input | verified
  const [starting, setStarting] = useState(false);
  const navigate = useNavigate();
  const polls = useRef(0);

  const check = useCallback(async () => {
    try {
      const { data } = await api.get("/identity/status");
      setStatus(data.id_verified ? "verified" : data.status || "not_started");
      return data.id_verified;
    } catch {
      setStatus("not_started");
      return false;
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login"); return; }
    check();
  }, [authLoading, user, navigate, check]);

  // If returning from Stripe (?done=1), poll a few times for the webhook to land.
  useEffect(() => {
    if (params.get("done") !== "1") return;
    setStatus("processing");
    const t = setInterval(async () => {
      polls.current += 1;
      const done = await check();
      if (done || polls.current >= 8) clearInterval(t);
    }, 2500);
    return () => clearInterval(t);
  }, [params, check]);

  const start = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/identity/session", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start verification");
      setStarting(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto px-6 py-20 text-center" data-testid="verify-identity-page">
      {status === "loading" && <Loader2 className="w-10 h-10 mx-auto text-neutral-400 animate-spin" />}

      {status === "verified" && (
        <>
          <ShieldCheck className="w-14 h-14 mx-auto text-[#FF4500]" />
          <h1 className="font-display font-black tracking-tighter text-4xl mt-6">You're verified</h1>
          <p className="text-neutral-600 mt-3">Your identity is confirmed. Hosts can trust you with the keys — book away.</p>
          <Button onClick={() => navigate("/search")} className="mt-8 bg-[#FF4500] hover:bg-[#E63E00] text-white rounded-full h-12 px-8" data-testid="verified-browse-btn">
            Find a space
          </Button>
        </>
      )}

      {status === "processing" && (
        <>
          <Loader2 className="w-12 h-12 mx-auto text-[#FF4500] animate-spin" />
          <h1 className="font-display font-black tracking-tighter text-3xl mt-6">Checking your documents…</h1>
          <p className="text-neutral-600 mt-3">This usually takes a moment. We'll unlock booking as soon as it clears.</p>
        </>
      )}

      {(status === "not_started" || status === "created" || status === "requires_input" || status === "canceled") && (
        <>
          <IdCard className="w-14 h-14 mx-auto text-[#FF4500]" />
          <h1 className="font-display font-black tracking-tighter text-4xl mt-6">Verify your identity</h1>
          <p className="text-neutral-600 mt-3">
            {status === "requires_input"
              ? "That attempt didn't go through. Let's try again — have your photo ID ready."
              : "A one-time photo ID + selfie check so hosts feel safe handing over the keys. Takes about a minute."}
          </p>
          <Button onClick={start} disabled={starting} className="mt-8 bg-[#0A0A0A] text-white hover:bg-neutral-800 rounded-full h-12 px-8" data-testid="start-verification-btn">
            {starting ? "Opening…" : "Verify with Stripe"}
          </Button>
          <p className="text-[11px] text-neutral-400 mt-4">Powered by Stripe Identity. We never see or store your ID images.</p>
        </>
      )}
    </div>
  );
}
